// Assembles Plotly traces for the multi-sensor overlay graph and parses
// Plotly relayout (zoom) events back into {from,to} ms ranges.

import { getTransientSeverity, TRANSIENT_SEVERITY_COLORS } from './pressureTraceUtils';
import { toEpochMs, toPlotTime, tzOffsetMs } from './trendResolutionUtils';

export { toPlotTime };

export const getSensorLabel = (sensor) => {
  if (!sensor) return '';
  return sensor.aqs_logger_id ? sensor.aqs_logger_id : `Sensor ${sensor.ID}`;
};

export const buildOverlayTraces = ({ sensorSeries, timeZone, projectTimezone, transientThresholds, showWaveforms }) => {
  const traces = [];

  (sensorSeries || []).forEach((s) => {
    const group = String(s.sensorId);

    if (s.displayReadings?.length) {
      traces.push({
        x: s.displayReadings.map((r) => toPlotTime(r.time_utc, timeZone, projectTimezone)),
        y: s.displayReadings.map((r) => parseFloat(r.measurement_m)),
        mode: 'lines',
        type: 'scatter',
        name: s.label,
        line: { color: s.color, width: 2 },
        legendgroup: group,
      });
    }

    (s.transients || []).forEach((tr) => {
      const minVal = parseFloat(tr.min_reading);
      const maxVal = parseFloat(tr.max_reading);
      if (isNaN(minVal) || isNaN(maxVal)) return;
      const severity = getTransientSeverity(maxVal - minVal, transientThresholds);
      const date = toPlotTime(tr.event_time, timeZone, projectTimezone);
      if (date === null) return;
      traces.push({
        x: [date, date],
        y: [minVal, maxVal],
        mode: 'lines',
        type: 'scatter',
        name: `${s.label} (transient)`,
        line: { color: TRANSIENT_SEVERITY_COLORS[severity], width: 3 },
        showlegend: false,
        legendgroup: group,
        // %{y} renders the y value AFTER the chart's unit conversion, keeping
        // the tooltip consistent with the axis; static text would bake meters.
        hovertemplate: `%{y:.2f}<br>Severity: ${severity}<extra>${s.label} (transient)</extra>`,
      });
    });

    if (showWaveforms) {
      (s.waveforms || []).forEach((w) => {
        if (!w.readings?.length) return;
        traces.push({
          x: w.readings.map((r) => toPlotTime(r.time_utc, timeZone, projectTimezone)),
          y: w.readings.map((r) => parseFloat(r.measurement_m)),
          mode: 'lines',
          type: 'scatter',
          name: `${s.label} (high-res)`,
          line: { color: s.color, width: 2.5 },
          showlegend: false,
          legendgroup: group,
        });
      });
    }
  });

  return traces;
};

/**
 * Keep only transients whose severity tier is enabled.
 * Transients with unparsable readings are kept — they carry no height to
 * classify, and buildOverlayTraces drops them from the chart anyway.
 *
 * @param {Array} transients          - raw transient rows ({min_reading, max_reading, ...})
 * @param {Object} enabledSeverities  - { low, medium, high } booleans
 * @param {Object} thresholds         - { med, high } absolute thresholds (meters)
 */
export const filterTransientsBySeverity = (transients, enabledSeverities, thresholds) =>
  (transients || []).filter((tr) => {
    const height = parseFloat(tr.max_reading) - parseFloat(tr.min_reading);
    if (isNaN(height)) return true;
    return enabledSeverities[getTransientSeverity(height, thresholds)] !== false;
  });

// Plotly reports zoom ranges as timezone-naive axis wall-clock strings in
// whatever timezone the traces were shifted to; undo that shift so the
// returned {from,to} are true UTC epochs comparable with raw event_time /
// time_utc values. The offset is evaluated at the axis value rather than the
// true epoch — they differ only inside the offset-sized window around a DST
// switch, which is negligible for zoom windows.
const axisValueToEpochMs = (value, timeZone, projectTimezone) => {
  const axisMs = typeof value === 'number' ? value : toEpochMs(value);
  if (isNaN(axisMs)) return NaN;
  return axisMs - tzOffsetMs(timeZone, projectTimezone, axisMs);
};

export const relayoutToRange = (event, timeZone, projectTimezone) => {
  if (!event) return undefined;
  if (event['xaxis.autorange']) return null;
  let from;
  let to;
  if (event['xaxis.range[0]'] != null && event['xaxis.range[1]'] != null) {
    from = event['xaxis.range[0]'];
    to = event['xaxis.range[1]'];
  } else if (Array.isArray(event['xaxis.range'])) {
    [from, to] = event['xaxis.range'];
  }
  if (from == null || to == null) return undefined;
  const fromMs = axisValueToEpochMs(from, timeZone, projectTimezone);
  const toMs = axisValueToEpochMs(to, timeZone, projectTimezone);
  if (isNaN(fromMs) || isNaN(toMs) || toMs <= fromMs) return undefined;
  return { from: fromMs, to: toMs };
};
